<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { RouterLink } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'

defineOptions({ inheritAttrs: false })

interface Props {
  to: RouteLocationRaw
}

const props = defineProps<Props>()
const attrs = useAttrs()

const anchorAttrs = computed(() => {
  const { onClick, ...rest } = attrs as Record<string, unknown>
  void onClick
  return rest
})

function isModifiedEvent(event: MouseEvent) {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey
}

function callUserClick(event: MouseEvent) {
  const handler = (attrs as Record<string, unknown>).onClick
  if (!handler) return
  if (Array.isArray(handler)) {
    for (const fn of handler) {
      if (typeof fn === 'function') {
        fn(event)
      }
    }
    return
  }
  if (typeof handler === 'function') {
    handler(event)
  }
}

function handleClick(
  event: MouseEvent,
  navigate: (e?: MouseEvent) => Promise<void | import('vue-router').NavigationFailure>
) {
  callUserClick(event)
  if (event.defaultPrevented) return
  if (event.button !== 0 || isModifiedEvent(event)) return
  const target = (attrs as Record<string, unknown>).target
  if (typeof target === 'string' && target !== '_self') return
  navigate(event)
}
</script>

<template>
  <RouterLink :to="props.to" custom v-slot="{ href, navigate }">
    <a :href="href" class="router-link-smart" v-bind="anchorAttrs"
      @click="(event) => handleClick(event, navigate)">
      <slot />
    </a>
  </RouterLink>
</template>

<style scoped>
.router-link-smart {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.router-link-smart:hover {
  background-color: var(--el-color-info-light-8);
  text-decoration: underline;
}
</style>

